/**
 * Tool-call orchestrator for AI Assistant Core
 *
 * ToolRegistry  — register / lookup tools
 * ToolOrchestrator — execute steps in parallel or serial order
 */

import type { ToolDefinition, ToolExecutor, ToolStep } from '../protocol'
import { generateId } from '../utils'

// ---------------------------------------------------------------------------
// ToolRegistry
// ---------------------------------------------------------------------------

export interface RegisteredTool {
  definition: ToolDefinition
  executor: ToolExecutor
}

export class ToolRegistry {
  private readonly tools = new Map<string, RegisteredTool>()

  /** Register a tool with its definition and executor. */
  register(definition: ToolDefinition, executor: ToolExecutor): void {
    this.tools.set(definition.name, { definition, executor })
  }

  /** Unregister a tool by name. */
  unregister(name: string): boolean {
    return this.tools.delete(name)
  }

  /** Look up a registered tool. */
  get(name: string): RegisteredTool | undefined {
    return this.tools.get(name)
  }

  /** Check whether a tool is registered. */
  has(name: string): boolean {
    return this.tools.has(name)
  }

  /** List all registered tool names. */
  list(): string[] {
    return Array.from(this.tools.keys())
  }

  /** Get all definitions (for sending to LLM). */
  getDefinitions(): ToolDefinition[] {
    return Array.from(this.tools.values()).map(t => t.definition)
  }

  /** Clear all registrations. */
  clear(): void {
    this.tools.clear()
  }
}

// ---------------------------------------------------------------------------
// ToolOrchestrator
// ---------------------------------------------------------------------------

export interface OrchestratorOptions {
  registry: ToolRegistry
  onStepStart?: (step: ToolStep) => void
  onStepProgress?: (stepId: string, percent: number, message?: string) => void
  onStepComplete?: (step: ToolStep) => void
  onStepError?: (step: ToolStep, error: Error) => void
}

export type ExecutionMode = 'parallel' | 'serial'

export interface ExecutionPlan {
  mode: ExecutionMode
  steps: Array<{
    toolName: string
    args: Record<string, unknown>
    title?: string
  }>
}

export class ToolOrchestrator {
  private readonly registry: ToolRegistry
  private readonly onStepStart?: (step: ToolStep) => void
  private readonly onStepComplete?: (step: ToolStep) => void
  private readonly onStepError?: (step: ToolStep, error: Error) => void

  constructor(options: OrchestratorOptions) {
    this.registry = options.registry
    this.onStepStart = options.onStepStart
    this.onStepComplete = options.onStepComplete
    this.onStepError = options.onStepError
  }

  /**
   * Execute a plan of tool steps.
   *
   * - `serial`:  steps run one after another; if one fails, abort the rest.
   * - `parallel`: steps run concurrently; all results collected regardless of individual failures.
   */
  async execute(plan: ExecutionPlan): Promise<ToolStep[]> {
    if (plan.mode === 'serial') {
      return this.executeSerial(plan.steps)
    }
    return this.executeParallel(plan.steps)
  }

  /** Execute a single tool step. */
  async executeSingle(
    toolName: string,
    args: Record<string, unknown>,
    title?: string
  ): Promise<ToolStep> {
    const registered = this.registry.get(toolName)
    if (!registered) {
      const step: ToolStep = {
        stepId: generateId('step_'),
        toolName,
        title: title ?? toolName,
        status: 'error',
        args,
        message: `Tool "${toolName}" is not registered`,
      }
      this.onStepError?.(step, new Error(step.message))
      return step
    }

    const step: ToolStep = {
      stepId: generateId('step_'),
      toolName,
      title: title ?? registered.definition.description ?? toolName,
      status: 'running',
      args,
    }

    this.onStepStart?.(step)

    const startTime = Date.now()
    try {
      const result = await registered.executor(args)
      step.status = 'success'
      step.result = result
      step.durationMs = Date.now() - startTime
      this.onStepComplete?.(step)
      return step
    } catch (err) {
      step.status = 'error'
      step.result = err instanceof Error ? err.message : String(err)
      step.durationMs = Date.now() - startTime
      this.onStepError?.(step, err instanceof Error ? err : new Error(String(err)))
      return step
    }
  }

  private async executeSerial(
    steps: ExecutionPlan['steps']
  ): Promise<ToolStep[]> {
    const results: ToolStep[] = []
    for (const s of steps) {
      const result = await this.executeSingle(s.toolName, s.args, s.title)
      results.push(result)
      if (result.status === 'error') {
        // Abort remaining serial steps
        break
      }
    }
    return results
  }

  private async executeParallel(
    steps: ExecutionPlan['steps']
  ): Promise<ToolStep[]> {
    return Promise.all(
      steps.map(s => this.executeSingle(s.toolName, s.args, s.title))
    )
  }
}
